import React, { Suspense, lazy, useEffect, useState } from "react"
import { Route, Routes, matchPath, Navigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"

import CookieConsent from "./CookieConsent"
import Footer from "./Footer"
import Header from "./Header"
import Loading from "./Loading"
import { useCookieConsent, getPage, getLocale } from "../helpers"
import { initialize } from "../helpers/gtmManager"

import { fetchData, selectData } from "../slices/dataSlice"

const { REACT_APP_DOMAIN_ZONE: _domainZone, REACT_APP_LANG: siteLang, REACT_APP_BRAND: brand } = process.env

const isItaly = _domainZone === "it"

const Home = isItaly ? lazy(() => import("../pages/ExternalGames")) : lazy(() => import("../pages/Home"))

const Games = lazy(() => import("../pages/Games"))
const ExternalGames = lazy(() => import("../pages/ExternalGames"))
const Info = lazy(() => import("../pages/Info"))
const SignUp = lazy(() => import("../pages/SignUp"))
const Promotions = lazy(() => import("../pages/Promotions"))
const LandingPage = lazy(() => import("../pages/LandingPage"))
const NotAvailablePage = lazy(() => import("../pages/NotAvailablePage"))

const hasExternalGames = process.env.REACT_APP_EXTERNAL_GAMES === "true"
const isSignUpHidden = process.env.REACT_APP_HIDE_SIGNUP === "true"
const locale = getLocale()
const formPath = path => `${locale ? `/${_domainZone}` : ""}${path.startsWith("/") ? path : `/${path}`}`
const domainZone = _domainZone.includes("-") ? _domainZone.substring(0, _domainZone.lastIndexOf("-")) : _domainZone
const contentDomainZone = _domainZone === "ca-eu" || _domainZone === "fr-eu" || _domainZone === "nz-eu" ? _domainZone : domainZone

const App = () => {
  const availableLang = siteLang?.split(",")
  const storedLang = availableLang?.length > 1 && sessionStorage.getItem("language")
  const defaultLang = siteLang ? availableLang[0] : contentDomainZone
  const [lang, setLang] = useState(storedLang || defaultLang)

  const handleLanguageChange = newLang => {
    sessionStorage.setItem("language", newLang)
    setLang(newLang)
    window.location.reload()
  }

  const contentfulLang = siteLang ? `${contentDomainZone}-${lang}` : contentDomainZone
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const data = useSelector(selectData)

  const { homePage, promotionsPage, promotions, headerNavigation, footerNavigation, translations, staticPages, logo, copyright } = data

  const isLangMenuAvailable = process.env.REACT_APP_SHOW_LANG_MENU === "true"
  const { showConsent, shouldLoadGtm, accept } = useCookieConsent()

  // Initialize GTM
  useEffect(() => {
    if (shouldLoadGtm) initialize()
  }, [shouldLoadGtm])

  // Fetch all data once
  useEffect(() => {
    dispatch(fetchData(contentfulLang))
  }, [dispatch, contentfulLang])

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  if (!homePage) return <Loading />

  const footerInfo = {
    footerNavigationItems: footerNavigation,
    copyright,
    licenceLogo: homePage?.licenceLogo,
    footerDisclaimer: homePage?.footerDisclaimer,
    footerLogos: homePage?.footerLogos
  }

  const loading = <Loading />
  const showLangMenu = isLangMenuAvailable

  if (translations && Object.keys(translations).length > 1) {
    const { slugs } = translations
    // const parts = pathname.split("/")
    // const gamesIndex = parts.indexOf(slugs.games)

    // const isSingleGamePage = gamesIndex !== -1 && !!parts[gamesIndex + 1]

    // const isFooterVisible = !isSingleGamePage || !isMobile

    const landingPagePath = matchPath(`/${promotions}/:id`, pathname)
    let landingData

    if (landingPagePath?.params?.id) {
      landingData = getPage(promotions, landingPagePath.params.id)
      if (landingData && !landingData.isLandingPage) landingData = null
    }

    if (!matchPath(`/${slugs.games}/:id`, pathname)) localStorage.removeItem("autoLaunchGameSlug")

    return (
      <div className={`page theme-${brand}`}>
        {pathname !== "/not-available" && (
          <Header
            list={!landingData ? headerNavigation : []}
            logo={homePage.logo}
            isHome={pathname === "/"}
            showLangMenu={showLangMenu}
            handleLanguageChange={siteLang && handleLanguageChange}
            availableLang={availableLang}
            landingData={landingData}
            pathname={pathname}
            userLang={lang}
            translations={translations}
            isItaly={isItaly}
          />
        )}
        {landingData ? (
          <Suspense fallback={loading}>
            <LandingPage {...getPage(promotions, landingPagePath.params.id)} />
          </Suspense>
        ) : (
          <main className={`page__body page__body--${domainZone}`}>
            <Suspense>
              <Routes>
                <Route exact path="/" element={locale ? <Navigate to={locale} replace /> : <Home />} />
                <Route exact path={formPath("/")} element={locale ? <Home /> : <Navigate to="/" replace />} />
                <Route path="/not-available" element={<NotAvailablePage logo={logo} />} />
                {hasExternalGames ? (
                  <>
                    <Route path={formPath(`${slugs.games}`)} element={<ExternalGames />} />
                    <Route path={formPath(`${slugs.games}/:id`)} element={<ExternalGames />} />
                    <Route path={formPath(`${slugs.games}/${slugs.category}/:category`)} element={<ExternalGames />} />
                  </>
                ) : (
                  <>
                    <Route path={formPath(`${slugs.games}`)} element={<Games />} />
                    <Route path={formPath(`${slugs.games}/:id`)} element={<Games />} />
                    <Route path={formPath(`${slugs.games}/${slugs.category}/:category`)} element={<Games />} />
                  </>
                )}

                <Route exact path={formPath(`/${promotionsPage?.slug}`)} element={<Promotions />} />
                <Route exact path={formPath(`/${promotionsPage?.slug}/:id`)} element={<Promotions />} />

                {!isSignUpHidden && <Route exact path={formPath(`/${slugs.signUp}`)} element={<SignUp />} />}

                <Route exact path={formPath("/:id")} element={<Info />} />
              </Routes>
            </Suspense>
          </main>
        )}

        {pathname !== "/not-available" && showConsent && (
          <CookieConsent onAccept={accept} cookiePolicy={staticPages.find(({ slug }) => slug === translations.slugs.cookiePolicy)?.pageContent} />
        )}

        {pathname !== "/not-available" && <Footer footerInfo={footerInfo} logo={homePage?.logo?.images?.icon?.url} isItaly={isItaly} />}
      </div>
    )
  }

  return null
}

export default App
